import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import { contactAPI } from '../api/api';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await contactAPI.sendMessage(formData);
      setSnackbar({
        open: true,
        message: 'Thank you for your message! We\'ll get back to you soon.',
        severity: 'success',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setSnackbar({
        open: true,
        message: 'We couldn\'t send your message. Please try again or email us directly.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: 'Email',
      content: 'info@alwaysinventingmore.com',
      href: 'mailto:info@alwaysinventingmore.com',
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 28 }} />,
      title: 'Location',
      content: 'United States',
      href: null,
    },
  ];

  const socialLinks = [
    {
      icon: <LinkedInIcon />,
      label: 'LinkedIn',
      href: 'https://linkedin.com',
    },
    {
      icon: <GitHubIcon />,
      label: 'GitHub',
      href: 'https://github.com',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '40vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: `
            radial-gradient(ellipse at 30% 30%, rgba(31, 111, 178, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 70%, rgba(47, 62, 78, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
          pt: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', animation: 'fadeInUp 0.8s ease-out' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              GET IN TOUCH
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 3,
                color: '#f8fafc',
              }}
            >
              Let's Build Something
              <Box component="span" sx={{ color: '#1f6fb2' }}> Great</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                fontSize: '1.1rem',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Have a project in mind? We'd love to hear about it. 
              Drop us a message and let's start a conversation.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, #111827 0%, #0a0f1a 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(17, 24, 39, 0.8) 100%)',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Send us a message
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                      mb: 4,
                    }}
                  >
                    Fill out the form below and we'll get back to you as soon as possible.
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          InputLabelProps={{
                            sx: { color: '#94a3b8' },
                          }}
                          InputProps={{
                            sx: { color: '#f8fafc' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          InputLabelProps={{
                            sx: { color: '#94a3b8' },
                          }}
                          InputProps={{
                            sx: { color: '#f8fafc' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          InputLabelProps={{
                            sx: { color: '#94a3b8' },
                          }}
                          InputProps={{
                            sx: { color: '#f8fafc' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Your Message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          multiline
                          rows={5}
                          InputLabelProps={{
                            sx: { color: '#94a3b8' },
                          }}
                          InputProps={{
                            sx: { color: '#f8fafc' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={submitting}
                          endIcon={<SendIcon />}
                          sx={{
                            px: 4,
                            py: 1.5,
                          }}
                        >
                          {submitting ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={5}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Contact Details */}
                {contactInfo.map((info, index) => (
                  <Card
                    key={index}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(17, 24, 39, 0.8) 100%)',
                      border: '1px solid rgba(148, 163, 184, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(31, 111, 178, 0.3)',
                        '& .contact-icon': {
                          color: '#1f6fb2',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box
                        className="contact-icon"
                        sx={{
                          color: '#64748b',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: 1,
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          {info.title.toUpperCase()}
                        </Typography>
                        {info.href ? (
                          <Typography
                            component="a"
                            href={info.href}
                            variant="body1"
                            sx={{
                              color: '#f8fafc',
                              textDecoration: 'none',
                              '&:hover': {
                                color: '#1f6fb2',
                              },
                            }}
                          >
                            {info.content}
                          </Typography>
                        ) : (
                          <Typography variant="body1" sx={{ color: '#f8fafc' }}>
                            {info.content}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* Social Links */}
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(17, 24, 39, 0.8) 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.08)',
                    flex: 1,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        letterSpacing: 1,
                        display: 'block',
                        mb: 2,
                      }}
                    >
                      FOLLOW US
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {socialLinks.map((social, index) => (
                        <Button
                          key={index}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          startIcon={social.icon}
                          sx={{
                            color: '#94a3b8',
                            borderColor: 'rgba(148, 163, 184, 0.2)',
                            '&:hover': {
                              borderColor: '#1f6fb2',
                              color: '#1f6fb2',
                              backgroundColor: 'rgba(31, 111, 178, 0.08)',
                            },
                          }}
                        >
                          {social.label}
                        </Button>
                      ))}
                    </Box>
                  </CardContent>
                </Card>

                {/* Quick Response Promise */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(31, 111, 178, 0.1) 0%, rgba(47, 62, 78, 0.1) 100%)',
                    border: '1px solid rgba(31, 111, 178, 0.2)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                      textAlign: 'center',
                      fontStyle: 'italic',
                    }}
                  >
                    "We typically respond within 24-48 hours. Looking forward to hearing from you!"
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Snackbar for form submission feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;

